import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { authActions, authStore } from '../../../store/authStore';
import { useSnapshot } from 'valtio';
import { AuthDebugger } from '../../../utils/authDebugger';
import { jwtDecode } from 'jwt-decode';
import { client } from '../../services/apollo';
import { CREATE_OIDC_USER } from '../../services/authService/queries';

export default function AuthBridge() {
  const auth = useAuth();
  const authSnapshot = useSnapshot(authStore);
  const lastAuthMethodRef = useRef<'oidc' | 'graphql' | null>(null);

  // 创建OIDC用户（Apollo）
  const createOidcUserApollo = async (
    email?: string,
    firstName?: string,
    lastName?: string,
    displayName?: string,
    accessToken?: string,
  ) => {
    try {
      if (!accessToken) {
        return null;
      }

      const { data, errors } = await client.mutate({
        mutation: CREATE_OIDC_USER,
        variables: { email, firstName, lastName, displayName },
        context: {
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        },
      });

      if (errors?.length) {
        return null;
      }

      const created = data?.createOidcUser;
      if (created) {
        AuthDebugger.logAuthChange('OIDC_BRIDGE', 'user_created_via_apollo', created);
        authActions.setUser(created);
        return created;
      }
      return null;
    } catch (error) {
      AuthDebugger.logAuthChange('OIDC_BRIDGE', 'user_creation_apollo_error', {
        error: error instanceof Error ? error.message : String(error),
      });
      return null;
    }
  };

  // 检测认证方法的来源
  const detectAuthSource = () => {
    if (auth.isAuthenticated && auth.user && !lastAuthMethodRef.current) {
      return 'oidc';
    }

    if (authSnapshot.isAuthenticated && lastAuthMethodRef.current === 'graphql') {
      return 'graphql';
    }

    return lastAuthMethodRef.current;
  };

  // 从OIDC用户对象中提取用户信息
  const extractUserInfo = (authUser: any) => {
    const profile = authUser?.profile || {};
    const idToken = authUser?.id_token || authUser?.idToken;

    // 尝试从ID token中提取信息
    let tokenClaims: any = {};
    if (idToken) {
      try {
        const decoded = JSON.parse(atob(idToken.split('.')[1]));
        tokenClaims = decoded;
      } catch (e) {}
    }

    // 提取用户信息，优先级：profile > token claims > 空字符串
    const userInfo = {
      email: profile.email || tokenClaims.email || tokenClaims['cognito:username'] || '',
      firstName:
        profile.given_name ||
        profile.first_name ||
        tokenClaims.given_name ||
        tokenClaims.first_name ||
        '',
      lastName:
        profile.family_name ||
        profile.last_name ||
        tokenClaims.family_name ||
        tokenClaims.last_name ||
        '',
      displayName:
        profile.name || profile.display_name || tokenClaims.name || tokenClaims.display_name || '',
    };

    return userInfo;
  };

  // 验证认证状态的完整性
  const isValidAuth = (tokens: any, user: any) => {
    const isValid = tokens?.accessToken && tokens?.idToken && tokens?.refreshToken && user?.email;

    AuthDebugger.logDecision(
      'AUTH_VALIDATION',
      isValid ? 'Valid authentication' : 'Invalid authentication',
      { tokens: !!tokens, user, isValid },
    );

    return isValid;
  };

  // 处理OIDC认证状态变化
  useEffect(() => {
    const currentAuthSource = detectAuthSource();

    if (auth.isAuthenticated && auth.user) {
      const oidcTokens = {
        accessToken: auth.user.access_token || '',
        idToken: auth.user.id_token || '',
        refreshToken: auth.user.refresh_token || '',
      };

      // 从 ID Token 提取声明作为兜底
      let claimsEmail = '';
      let claimsGiven = '';
      let claimsFamily = '';
      let claimsName = '';
      try {
        if (oidcTokens.idToken) {
          const decoded: any = jwtDecode(oidcTokens.idToken);
          claimsEmail = decoded?.email || '';
          claimsGiven = decoded?.given_name || '';
          claimsFamily = decoded?.family_name || '';
          claimsName = decoded?.name || '';
        }
      } catch (e) {
        // ignore decode errors
      }

      const email = auth.user.profile?.email || claimsEmail || '';
      const firstName = auth.user.profile?.given_name || claimsGiven || '';
      const lastName = auth.user.profile?.family_name || claimsFamily || '';
      const displayName =
        auth.user.profile?.name || claimsName || firstName || (email ? email.split('@')[0] : '');

      const oidcUserProfile = { email, firstName, lastName, displayName };

      AuthDebugger.logAuthChange('OIDC', 'authentication_detected', {
        oidcTokens,
        oidcUserProfile,
        currentAuthSource,
      });

      // Check for valid GraphQL auth first - don't require currentAuthSource check
      // This allows email/password logins to be preserved even if OIDC tokens exist in localStorage
      const hasValidGraphQLAuth =
        authSnapshot.isAuthenticated && isValidAuth(authSnapshot.tokens, authSnapshot.user);

      const hasValidOIDCAuth = isValidAuth(oidcTokens, oidcUserProfile);
      const hasMinimalOIDCAuth = !!(oidcTokens.accessToken && oidcTokens.idToken);

      // IMPORTANT: Only use OIDC auth if there's NO valid GraphQL auth
      // This prevents OIDC from overriding email/password logins on page refresh
      if (!hasValidGraphQLAuth && (hasValidOIDCAuth || hasMinimalOIDCAuth)) {
        AuthDebugger.logDecision(
          'USE_OIDC_AUTH',
          'No valid GraphQL auth, using OIDC/minimal tokens',
          { hasValidGraphQLAuth, hasValidOIDCAuth, hasMinimalOIDCAuth },
        );

        lastAuthMethodRef.current = 'oidc';

        // 设置认证状态
        authActions.setAuth(oidcTokens, oidcUserProfile, 'OIDC_BRIDGE');

        // 尝试创建用户（异步，不阻塞UI）
        // 注意：传递 idToken 而不是 accessToken，因为 ID token 包含用户信息
        createOidcUserApollo(
          // email 可能为空，后端会从 ID token 中解析
          oidcUserProfile.email || undefined,
          oidcUserProfile.firstName || undefined,
          oidcUserProfile.lastName || undefined,
          oidcUserProfile.displayName || undefined,
          oidcTokens.idToken, // ← 使用 ID token，包含用户 profile 信息
        ).catch(error => {});
      } else if (hasValidGraphQLAuth) {
        AuthDebugger.logDecision('PRESERVE_GRAPHQL_AUTH', 'Valid GraphQL auth exists, preserving', {
          graphqlUser: authSnapshot.user?.email,
          oidcUser: oidcUserProfile.email,
        });

        // Clear OIDC session if it exists to prevent future conflicts
        if (auth.isAuthenticated) {
          // Remove OIDC user from react-oidc-context
          auth.removeUser().catch(err => {});

          // Also clear OIDC-related items from localStorage
          // react-oidc-context stores tokens with specific keys
          const oidcKeys = Object.keys(localStorage).filter(
            key =>
              key.startsWith('oidc.') ||
              key.includes('cognito') ||
              key.includes('id_token') ||
              key.includes('access_token'),
          );

          oidcKeys.forEach(key => {
            localStorage.removeItem(key);
          });
        }
      } else {
        AuthDebugger.logDecision('NO_VALID_AUTH', 'No valid authentication available', {
          hasValidGraphQLAuth,
          hasValidOIDCAuth,
        });
      }
    } else if (!auth.isAuthenticated) {
      // Check for ANY valid auth - preserve both OIDC and GraphQL logins
      // Don't require currentAuthSource check - tokens/user validity is enough
      const hasValidAuth =
        authSnapshot.isAuthenticated && isValidAuth(authSnapshot.tokens, authSnapshot.user);

      if (!hasValidAuth && authSnapshot.isAuthenticated) {
        AuthDebugger.logDecision(
          'CLEAR_AUTH_STATE',
          'OIDC not authenticated and no valid tokens/user, clearing auth state',
          {
            hasValidAuth,
            hasTokens: !!authSnapshot.tokens.accessToken,
            hasUser: !!authSnapshot.user,
          },
        );

        lastAuthMethodRef.current = null;
        authActions.logout();
      } else if (hasValidAuth) {
        AuthDebugger.logDecision(
          'PRESERVE_VALID_AUTH',
          'OIDC not authenticated, but valid tokens/user exist, preserving auth',
          {
            userEmail: authSnapshot.user?.email,
            hasAccessToken: !!authSnapshot.tokens.accessToken,
            lastAuthMethod: lastAuthMethodRef.current,
          },
        );
        // Keep the authentication, it's valid even if OIDC session is not active
      }
    }
  }, [auth.isAuthenticated, auth.user]);

  // 监听authStore变化，跟踪认证方法
  useEffect(() => {
    if (
      authSnapshot.isAuthenticated &&
      isValidAuth(authSnapshot.tokens, authSnapshot.user) &&
      lastAuthMethodRef.current !== 'oidc'
    ) {
      if (authSnapshot.user?.id || authSnapshot.user?.cognitoId) {
        AuthDebugger.logDecision('DETECT_GRAPHQL_AUTH', 'Detected GraphQL authentication', {
          userId: authSnapshot.user?.id,
          cognitoId: authSnapshot.user?.cognitoId,
          email: authSnapshot.user?.email,
        });

        lastAuthMethodRef.current = 'graphql';
      }
    }
  }, [authSnapshot.isAuthenticated, authSnapshot.user, authSnapshot.tokens]);

  // 处理OIDC错误
  useEffect(() => {
    if (auth.error) {
      AuthDebugger.logAuthChange('OIDC', 'error_detected', { error: auth.error });

      // If GraphQL tokens/user are valid, DO NOT logout on OIDC errors.
      const hasValidGraphQLAuth =
        authSnapshot.isAuthenticated &&
        !!(
          authSnapshot.tokens?.accessToken &&
          authSnapshot.tokens?.refreshToken &&
          authSnapshot.user?.email
        );

      if (hasValidGraphQLAuth) {
        AuthDebugger.logDecision(
          'PRESERVE_ON_OIDC_ERROR',
          'OIDC error detected, preserving valid GraphQL session',
          { lastAuthMethod: lastAuthMethodRef.current },
        );
        return;
      }

      if (lastAuthMethodRef.current === 'oidc') {
        AuthDebugger.logDecision(
          'CLEAR_OIDC_AUTH',
          'OIDC error detected and no valid GraphQL session; logging out',
          { lastAuthMethod: lastAuthMethodRef.current },
        );
        lastAuthMethodRef.current = null;
        authActions.logout();
      }
    }
  }, [auth.error]);

  // 调试信息
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      AuthDebugger.logAuthChange('AuthBridge', 'state_update', {
        oidcAuthenticated: auth.isAuthenticated,
        authStoreAuthenticated: authSnapshot.isAuthenticated,
        lastAuthMethod: lastAuthMethodRef.current,
        hasOidcUser: !!auth.user,
        hasAuthStoreUser: !!authSnapshot.user,
        authStoreUserEmail: authSnapshot.user?.email,
        oidcUserEmail: auth.user?.profile?.email,
      });
    }
  }, [auth.isAuthenticated, authSnapshot.isAuthenticated, auth.user, authSnapshot.user]);

  return null;
}
